import type { ApolloClient } from '@apollo/client';
import { from, map } from 'rxjs';
import {
    AlarmExceedancesRuntimeDataDocument,
    SwarmType,
    type AlarmExceedancesRuntimeDataQuery,
    type AlarmExceedancesRuntimeDataQueryVariables,
} from '@/graphql/types';
import { DATA_TYPE } from '../../../../../../graph/enums';
import type { RuntimeDataServiceInput, RuntimeDataServiceOutput } from '../contracts';
import { RuntimeDataService } from '../types';
import type {
    RuntimeDataServiceRequest,
    RuntimeResultLinkDescriptor,
    RuntimeTabularCellRenderOptions,
    RuntimeTabularColumnDescriptor,
} from '../types';
import type { TableLayoutShape } from '../../../elements/table/contracts';
import {
    buildRowsFromColumns,
    getColumnIndexesForKeysOrThrow,
    pickTableSettingsByColumnIndexes,
} from '../tabular';
import type { GraphTimeWindow, RenderContext } from '../../../elements/types';
import {
    isRecord,
    normalizeNullableInteger,
    normalizeOptionalPositiveInteger,
    normalizeOrderedTimeWindow,
    normalizeRequiredFiniteNumber,
    normalizeRequiredId,
    normalizeRequiredInteger,
    parseOptionalPositiveIntegerArg,
} from '../validationPrimitives';

const RELAY_MEASURING_POINT_TYPE = 'VibrationMeasuringPoint';
const ALARM_EXCEEDANCE_LIMIT_ARG_OPTIONS = Array.from({ length: 100 / 5 }, (_unused, index) => {
    const value = String((index + 1) * 5);
    return [value, value] as const;
});
type AlarmExceedanceEntry =
    RuntimeDataServiceOutput<'measuring_point.alarm_exceedances'>['entries'][number];
type AlarmSummaryEntry =
    RuntimeDataServiceOutput<'measuring_point.alarm_exceedances'>['alarms'][number];

function toMeasuringPointRelayId(measuringPointId: string): string {
    return btoa(`${RELAY_MEASURING_POINT_TYPE}:${measuringPointId}`);
}

function formatLimitReachedNote(limitReachedValue: number | null): string | null {
    if (limitReachedValue === null) {
        return null;
    }

    return interpolate(
        gettext('ALARM_EXCEEDANCES_LIMIT_REACHED_NOTE'),
        { limit: limitReachedValue },
        true
    );
}

function normalizeAlarmExceedanceAxis(
    value: unknown,
    pathLabel: string
): RuntimeDataServiceOutput<'measuring_point.alarm_exceedances'>['entries'][number]['x'] {
    if (value === null) {
        return null;
    }

    if (!isRecord(value)) {
        throw new Error(`Expected "${pathLabel}" to be an object or null.`);
    }

    return {
        vtop: normalizeRequiredFiniteNumber(value.vtop, `${pathLabel}.vtop`),
        fdom: normalizeRequiredFiniteNumber(value.fdom, `${pathLabel}.fdom`),
    };
}

function normalizeAlarmSummaryEntry(
    value: unknown,
    pathLabel: string
): RuntimeDataServiceOutput<'measuring_point.alarm_exceedances'>['alarms'][number] {
    if (!isRecord(value)) {
        throw new Error(`Expected "${pathLabel}" to be an object.`);
    }

    if (typeof value.name !== 'string') {
        throw new Error(`Expected "${pathLabel}.name" to be a string.`);
    }

    return {
        name: value.name,
        counts: normalizeRequiredInteger(value.counts, `${pathLabel}.counts`),
        triggerPct: normalizeRequiredFiniteNumber(value.triggerPct, `${pathLabel}.triggerPct`),
        triggerMm: normalizeRequiredFiniteNumber(value.triggerMm, `${pathLabel}.triggerMm`),
    };
}

function normalizeAlarmExceedanceEntry(
    value: unknown,
    pathLabel: string
): RuntimeDataServiceOutput<'measuring_point.alarm_exceedances'>['entries'][number] {
    if (!isRecord(value)) {
        throw new Error(`Expected "${pathLabel}" to be an object.`);
    }

    return {
        timestamp: normalizeRequiredFiniteNumber(value.timestamp, `${pathLabel}.timestamp`),
        x: normalizeAlarmExceedanceAxis(value.x, `${pathLabel}.x`),
        y: normalizeAlarmExceedanceAxis(value.y, `${pathLabel}.y`),
        z: normalizeAlarmExceedanceAxis(value.z, `${pathLabel}.z`),
        vector: normalizeAlarmExceedanceAxis(value.vector, `${pathLabel}.vector`),
    };
}

function validateAlarmExceedancesInput(
    input: unknown
): RuntimeDataServiceInput<'measuring_point.alarm_exceedances'> {
    if (!isRecord(input)) {
        throw new Error('Expected "measuring_point.alarm_exceedances" input to be an object.');
    }

    const { startTime, endTime } = normalizeOrderedTimeWindow(
        input.startTime,
        input.endTime,
        'measuring_point.alarm_exceedances'
    );

    const validatedLimit = normalizeOptionalPositiveInteger(
        input.limit,
        'measuring_point.alarm_exceedances.limit'
    );

    return {
        measuringPointId: normalizeRequiredId(
            input.measuringPointId,
            'measuring_point.alarm_exceedances.measuringPointId'
        ),
        startTime,
        endTime,
        ...(typeof validatedLimit === 'undefined' ? {} : { limit: validatedLimit }),
    };
}

function validateAlarmExceedancesResult(
    result: unknown
): RuntimeDataServiceOutput<'measuring_point.alarm_exceedances'> {
    if (!isRecord(result)) {
        throw new Error('Expected "measuring_point.alarm_exceedances" result to be an object.');
    }

    if (!Array.isArray(result.alarms)) {
        throw new Error('Expected "measuring_point.alarm_exceedances.alarms" to be an array.');
    }

    if (!Array.isArray(result.entries)) {
        throw new Error('Expected "measuring_point.alarm_exceedances.entries" to be an array.');
    }

    if (typeof result.isFlatLine !== 'boolean') {
        throw new Error('Expected "measuring_point.alarm_exceedances.isFlatLine" to be a boolean.');
    }

    if (typeof result.limitReached !== 'boolean') {
        throw new Error(
            'Expected "measuring_point.alarm_exceedances.limitReached" to be a boolean.'
        );
    }

    const limitReachedValue = normalizeNullableInteger(
        result.limitReachedValue,
        'measuring_point.alarm_exceedances.limitReachedValue'
    );

    return {
        alarms: result.alarms.map((entry, index) =>
            normalizeAlarmSummaryEntry(entry, `measuring_point.alarm_exceedances.alarms[${index}]`)
        ),
        isFlatLine: result.isFlatLine,
        entries: result.entries.map((entry, index) =>
            normalizeAlarmExceedanceEntry(
                entry,
                `measuring_point.alarm_exceedances.entries[${index}]`
            )
        ),
        limitReached: result.limitReached,
        limitReachedValue,
        limitReachedNote: formatLimitReachedNote(limitReachedValue),
    };
}

const ALARM_EXCEEDANCE_AXES = ['x', 'y', 'z', 'vector'] as const;

type AlarmExceedanceAxisName = (typeof ALARM_EXCEEDANCE_AXES)[number];

function createAxisMeasurementColumn(
    axis: AlarmExceedanceAxisName,
    measure: 'fdom' | 'vtop',
    dataType: string
): RuntimeTabularColumnDescriptor<AlarmExceedanceEntry> {
    return {
        kind: 'measurement',
        key: `${axis}_${measure}`,
        dataType,
        qualifier: axis,
        defaultAlignment: 'right',
        // `resolve_alarm_exceedances` passes `get_highest_values=False`, so `sbr_gather_stats`
        // has already run `_convert_distance_unit` over the Vtop values. Fdom is a frequency
        // and is never converted on either side.
        ...(measure === 'vtop' ? { preConverted: true as const } : {}),
        resolveCell: (entry: AlarmExceedanceEntry) => entry[axis]?.[measure],
    };
}

type AlarmExceedanceColumn = RuntimeTabularColumnDescriptor<AlarmExceedanceEntry>;

function createAlarmExceedanceColumns(): readonly AlarmExceedanceColumn[] {
    return [
        {
            kind: 'timestamp',
            key: 'timestamp',
            label: gettext('TIMESTAMP'),
            defaultAlignment: 'left',
            resolveCell: (entry: AlarmExceedanceEntry) => entry.timestamp,
        },
        ...ALARM_EXCEEDANCE_AXES.flatMap((axis) => [
            createAxisMeasurementColumn(axis, 'fdom', DATA_TYPE.FDOM),
            createAxisMeasurementColumn(axis, 'vtop', DATA_TYPE.VTOP),
        ]),
    ];
}

function createAlarmSummaryColumns() {
    return [
        {
            kind: 'text',
            key: 'name',
            label: gettext('ALARM'),
            defaultAlignment: 'left',
            resolveCell: (entry: AlarmSummaryEntry) => entry.name,
        },
        {
            kind: 'text',
            key: 'trigger_pct',
            label: gettext('TRIGGER_PERCENT'),
            defaultAlignment: 'right',
            resolveCell: (entry: AlarmSummaryEntry) => `${entry.triggerPct}%`,
        },
        {
            // The trigger level is expressed in the Vtop unit but is not a Vtop reading, so
            // it keeps its own name and only borrows the unit, rounding and conversion.
            kind: 'measurement',
            key: 'trigger_mm',
            dataType: DATA_TYPE.VTOP,
            label: gettext('ALARM_TRIGGER_LEVEL'),
            defaultAlignment: 'right',
            resolveCell: (entry: AlarmSummaryEntry) => entry.triggerMm,
        },
        {
            kind: 'count',
            key: 'counts',
            label: gettext('EXCEEDANCE_COUNT'),
            defaultAlignment: 'right',
            resolveCell: (entry: AlarmSummaryEntry) => entry.counts,
        },
    ] as const satisfies readonly RuntimeTabularColumnDescriptor<AlarmSummaryEntry>[];
}

type AlarmExceedanceColumnKey = ReturnType<typeof createAlarmExceedanceColumns>[number]['key'];

function isVectorColumn(columnKey: AlarmExceedanceColumnKey): boolean {
    return columnKey === 'vector_fdom' || columnKey === 'vector_vtop';
}

function buildAlarmExceedanceEntriesShape(
    settings: TableLayoutShape,
    entries: RuntimeDataServiceOutput<'measuring_point.alarm_exceedances'>['entries'],
    columns: readonly RuntimeTabularColumnDescriptor<AlarmExceedanceEntry>[],
    options: RuntimeTabularCellRenderOptions
): TableLayoutShape {
    const shouldHideVectorColumns = entries.every((entry) => entry.vector === null);
    const activeColumnKeys = columns
        .map((column) => column.key)
        .filter(
            (columnKey): columnKey is AlarmExceedanceColumnKey =>
                !shouldHideVectorColumns || !isVectorColumn(columnKey)
        );
    const activeColumnIndexes = getColumnIndexesForKeysOrThrow(columns, activeColumnKeys);

    if (activeColumnIndexes.length === 0) {
        throw new Error(
            'Expected at least one active table column after applying vector visibility rules.'
        );
    }

    const rows = buildRowsFromColumns(entries, columns, activeColumnKeys, options);
    const projectedSettings = pickTableSettingsByColumnIndexes(settings, activeColumnIndexes);

    return {
        header: projectedSettings.header,
        rows,
        columnAlignments: projectedSettings.columnAlignments,
        columnWidths: projectedSettings.columnWidths,
    };
}

function parseEntriesLinkValue(
    value: unknown
): RuntimeDataServiceOutput<'measuring_point.alarm_exceedances'>['entries'] {
    return validateAlarmExceedancesResult({
        alarms: [],
        isFlatLine: true,
        entries: value,
        limitReached: false,
        limitReachedValue: null,
    }).entries;
}

function parseAlarmsLinkValue(
    value: unknown
): RuntimeDataServiceOutput<'measuring_point.alarm_exceedances'>['alarms'] {
    return validateAlarmExceedancesResult({
        alarms: value,
        isFlatLine: true,
        entries: [],
        limitReached: false,
        limitReachedValue: null,
    }).alarms;
}

function buildAlarmExceedancesInputFromContext({
    context,
    graphTimeWindow,
    label,
}: {
    context?: RenderContext;
    graphTimeWindow?: GraphTimeWindow;
    label: string;
}): Omit<RuntimeDataServiceInput<'measuring_point.alarm_exceedances'>, 'limit'> {
    if (!context || context.kind !== 'measuring-point') {
        throw new Error(`${label} requires measuring-point context.`);
    }

    if (!graphTimeWindow) {
        throw new Error(`Graph runtime settings are required to resolve ${label}.`);
    }

    return {
        measuringPointId: context.measuringPoint.id,
        startTime: graphTimeWindow.viewStartTs,
        endTime: graphTimeWindow.viewEndTs,
    };
}

export class AlarmExceedancesRuntimeDataService extends RuntimeDataService<'measuring_point.alarm_exceedances'> {
    static readonly serviceId = 'measuring_point.alarm_exceedances' as const;

    static get serviceMetadata() {
        return {
            label: gettext('DATA_SERVICE_ALARM_EXCEEDANCES_LABEL'),
            description: gettext('DATA_SERVICE_ALARM_EXCEEDANCES_DESCRIPTION'),
        } as const;
    }

    static get serviceResultLinks() {
        const alarmExceedanceColumns = createAlarmExceedanceColumns();
        const alarmSummaryColumns = createAlarmSummaryColumns();

        return [
            {
                key: 'entries',
                label: gettext('DATA_SERVICE_ALARM_EXCEEDANCES_ENTRIES_LABEL'),
                description: gettext('DATA_SERVICE_ALARM_EXCEEDANCES_ENTRIES_DESCRIPTION'),
                supportedContextKinds: ['measuring-point'],
                supportedSwarmTypes: [SwarmType.Vibration],
                path: 'entries',
                valueKind: 'tabular',
                argPolicy: 'optional',
                argOptions: ALARM_EXCEEDANCE_LIMIT_ARG_OPTIONS,
                exposeInInsertionPresets: true,
                columns: alarmExceedanceColumns,
                parseTableRows: parseEntriesLinkValue,
                buildTableShape: (settings, linkValue, _context, options) =>
                    buildAlarmExceedanceEntriesShape(
                        settings,
                        parseEntriesLinkValue(linkValue),
                        alarmExceedanceColumns,
                        options
                    ),
                buildInput: ({ context, graphTimeWindow, arg }) => {
                    const parsedLimit = parseOptionalPositiveIntegerArg(
                        arg,
                        'measuring_point.alarm_exceedances.entries.arg'
                    );

                    return {
                        ...buildAlarmExceedancesInputFromContext({
                            context,
                            graphTimeWindow,
                            label: 'Alarm exceedance entries',
                        }),
                        ...(typeof parsedLimit === 'undefined' ? {} : { limit: parsedLimit }),
                    };
                },
            },
            {
                key: 'alarms',
                label: gettext('DATA_SERVICE_ALARM_EXCEEDANCES_CONFIGURED_ALARMS_LABEL'),
                description: gettext(
                    'DATA_SERVICE_ALARM_EXCEEDANCES_CONFIGURED_ALARMS_DESCRIPTION'
                ),
                supportedContextKinds: ['measuring-point'],
                supportedSwarmTypes: [SwarmType.Vibration],
                path: 'alarms',
                valueKind: 'tabular',
                argPolicy: 'forbidden',
                exposeInInsertionPresets: true,
                columns: alarmSummaryColumns,
                parseTableRows: parseAlarmsLinkValue,
                buildInput: ({ context, graphTimeWindow }) =>
                    buildAlarmExceedancesInputFromContext({
                        context,
                        graphTimeWindow,
                        label: 'Configured alarms binding',
                    }),
            },
            {
                key: 'limit_reached_note',
                label: gettext('DATA_SERVICE_ALARM_EXCEEDANCES_LIMIT_REACHED_NOTE_LABEL'),
                description: gettext(
                    'DATA_SERVICE_ALARM_EXCEEDANCES_LIMIT_REACHED_NOTE_DESCRIPTION'
                ),
                supportedContextKinds: ['measuring-point'],
                supportedSwarmTypes: [SwarmType.Vibration],
                path: 'limitReachedNote',
                valueKind: 'scalar',
                argPolicy: 'optional',
                argOptions: ALARM_EXCEEDANCE_LIMIT_ARG_OPTIONS,
                exposeInInsertionPresets: true,
                requiresGraphTimeWindow: true,
                buildInput: ({ context, graphTimeWindow, arg }) => {
                    const parsedLimit = parseOptionalPositiveIntegerArg(
                        arg,
                        'measuring_point.alarm_exceedances.limit_reached_note.arg'
                    );

                    return {
                        ...buildAlarmExceedancesInputFromContext({
                            context,
                            graphTimeWindow,
                            label: 'Limit reached note binding',
                        }),
                        ...(typeof parsedLimit === 'undefined' ? {} : { limit: parsedLimit }),
                    };
                },
            },
        ] as const satisfies readonly RuntimeResultLinkDescriptor<'measuring_point.alarm_exceedances'>[];
    }

    constructor(private readonly client: ApolloClient<object>) {
        super();
    }

    validateArgs = validateAlarmExceedancesInput;

    validateResult = validateAlarmExceedancesResult;

    createResult$ = ({ input }: RuntimeDataServiceRequest<'measuring_point.alarm_exceedances'>) =>
        from(
            this.client.query<
                AlarmExceedancesRuntimeDataQuery,
                AlarmExceedancesRuntimeDataQueryVariables
            >({
                query: AlarmExceedancesRuntimeDataDocument,
                variables: {
                    measuringPointId: toMeasuringPointRelayId(input.measuringPointId),
                    startTime: input.startTime,
                    endTime: input.endTime,
                    ...(typeof input.limit === 'undefined' ? {} : { limit: input.limit }),
                },
                fetchPolicy: 'cache-first',
            })
        ).pipe(map(({ data }) => validateAlarmExceedancesResult(data.alarmExceedances)));
}

export function createAlarmExceedancesService(
    client: ApolloClient<object>
): AlarmExceedancesRuntimeDataService {
    return new AlarmExceedancesRuntimeDataService(client);
}
